import React, { useMemo, useState } from 'react';

import PlanActualSegmented from 'components/common/PlanActualSegmented';
import {
  RESISTANCE_EMPTY_MESSAGE,
  RESISTANCE_MISSING_SOURCE_MESSAGE,
} from 'constants/messageConstants';
import {
  PLAN_ACTUAL_ACTUAL_LABEL,
  PLAN_ACTUAL_PLAN_LABEL,
  RESISTANCE_CURRENT_COLUMN_LABEL,
  RESISTANCE_GROUP_COLUMN_LABEL,
  RESISTANCE_HISTORY_NOTE,
  RESISTANCE_HISTORY_SOURCE_LABEL,
  RESISTANCE_PREVIOUS_COLUMN_LABEL,
} from 'constants/uiConstants';
import { useScopedInputs } from 'hooks/useScopedInputs';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { computeResistance } from 'pages/FarmsPage/Workspace/Insights/computeResistance';

/** A count, or a dash for none — the same convention as the other Live totals tables. */
const CountValue: React.FC<{ count: number }> = ({ count }) =>
  count > 0 ? <>{count}</> : <span className="text-gray-muted">—</span>;

/**
 * Live totals › Resistance (OP-2563): how many applications use each mode of
 * action group in the plans in scope, and on the same paddocks in the 3
 * years before. Follows the tree selection and year filter like the other
 * sections, and updates as tasks are saved. Earlier years count Actual
 * (default) or Plan applications — the user picks, and the column header and
 * note always name the source.
 *
 * Stays visible with an explanation when there's nothing to count, rather
 * than disappearing.
 */
const ResistancePanel: React.FC = () => {
  const { scopedInputs, orgInputs, isLoading } = useScopedInputs();
  const [historySource, setHistorySource] = useState<InputTaskCategory>(
    InputTaskCategory.Actual
  );
  const sourceLabel =
    historySource === InputTaskCategory.Actual
      ? PLAN_ACTUAL_ACTUAL_LABEL
      : PLAN_ACTUAL_PLAN_LABEL;

  const summary = useMemo(
    () =>
      isLoading
        ? null
        : computeResistance(scopedInputs, orgInputs, historySource),
    [isLoading, scopedInputs, orgInputs, historySource]
  );

  if (!summary) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-base text-gray-muted">Loading...</span>
      </div>
    );
  }

  if (summary.rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-center">
        <span className="text-base text-gray-dark">{RESISTANCE_EMPTY_MESSAGE}</span>
      </div>
    );
  }

  const { previousYears, earlierPlansWithoutSource } = summary;
  const previousLabel =
    previousYears.length > 0
      ? RESISTANCE_PREVIOUS_COLUMN_LABEL(
          previousYears[0],
          previousYears[previousYears.length - 1],
          sourceLabel
        )
      : null;

  return (
    <div className="flex flex-col gap-3">
      {previousLabel && (
        <div className="flex items-center justify-end gap-2">
          <span className="text-sm text-gray-dark">
            {RESISTANCE_HISTORY_SOURCE_LABEL}
          </span>
          <PlanActualSegmented value={historySource} onChange={setHistorySource} />
        </div>
      )}
      <table className="w-full text-base">
        <thead>
          <tr className="border-b">
            <th className="pb-1.5 pr-3 text-left text-sm font-semibold text-gray-muted">
              {RESISTANCE_GROUP_COLUMN_LABEL}
            </th>
            <th className="pb-1.5 pl-2 text-right text-sm font-semibold text-gray-muted">
              {RESISTANCE_CURRENT_COLUMN_LABEL}
            </th>
            {previousLabel && (
              <th className="pb-1.5 pl-2 text-right text-sm font-semibold text-gray-muted">
                {previousLabel}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {summary.rows.map((row, idx) => (
            <tr
              key={row.group}
              className={`hover:bg-rowHover ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
            >
              <td className="py-1 pr-3 text-label text-charcoal">
                {row.group}
              </td>
              <td className="py-1 pl-2 text-right tabular-nums text-charcoal">
                <CountValue count={row.current} />
              </td>
              {previousLabel && (
                <td className="py-1 pl-2 text-right tabular-nums text-charcoal">
                  <CountValue count={row.previous} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {previousLabel && (
        <p className="text-sm text-gray-dark">
          {RESISTANCE_HISTORY_NOTE(sourceLabel)}
          {earlierPlansWithoutSource > 0 &&
            ` ${RESISTANCE_MISSING_SOURCE_MESSAGE(earlierPlansWithoutSource, sourceLabel)}`}
        </p>
      )}
    </div>
  );
};

export default ResistancePanel;
